import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight, CheckCircle2, Sparkles } from "lucide-react";
import SectionHeading from "@/components/SectionHeading";
import Tilt3D from "@/components/Tilt3D";
import Hero3D from "@/components/Hero3D";
import { COMPANY, SERVICES, INDUSTRIES, TESTIMONIALS } from "@/data/content";

const STAFFING_IMG =
  "https://images.pexels.com/photos/4344340/pexels-photo-4344340.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=720";

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden min-h-[92vh] flex items-center isolate">
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-gradient-to-b from-bone via-bone to-[#EEE5E8]" />
        </div>
        {/* 3D scene positioned in the middle/right — hidden on mobile */}
        <div className="absolute inset-y-0 right-0 w-1/2 z-[1] pointer-events-none hidden lg:block">
          <div className="relative w-full h-full pointer-events-auto">
            <Hero3D />
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-6 md:px-12 pt-16 md:pt-24 pb-24 md:pb-32 w-full relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7 animate-fade-up">              <div className="overline text-terracotta mb-6 flex items-center gap-2">
                <Sparkles className="w-3.5 h-3.5" strokeWidth={1.5} /> Since {COMPANY.founded} · India
              </div>
              <h1 className="font-display font-medium text-5xl sm:text-6xl lg:text-7xl leading-[0.98] tracking-tight text-ink">
                Great companies
                <br />
                are built on
                <br />
                <span className="text-forest italic font-light">great hires.</span>
              </h1>
              <p className="mt-8 max-w-xl text-lg text-muted2 leading-relaxed">
                Novacore Services Pvt Ltd is an HR &amp; recruitment firm helping ambitious
                companies hire faster, pay accurately, and stay compliant — across
                permanent, contract, RPO, and payroll mandates.
              </p>
              <div className="mt-10 flex flex-wrap gap-4">
                <Link to="/contact" data-testid="hero-cta-primary" className="btn-primary">
                  Get a workforce plan
                  <ArrowUpRight className="w-4 h-4" strokeWidth={1.5} />
                </Link>
                <Link to="/services" data-testid="hero-cta-secondary" className="btn-ghost">
                  Explore services
                  <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
                </Link>
              </div>
            </div>

            <div className="lg:col-span-5 hidden lg:block" aria-hidden />
          </div>

          {/* Stats bar */}
          <div className="mt-24 grid grid-cols-2 md:grid-cols-4 gap-8 border-t border-hairline/60 pt-10 backdrop-blur-sm">
            {[
              { k: "500+", v: "Candidates placed" },
              { k: "40+", v: "Clients served" },
              { k: "95%", v: "Client retention" },
              { k: "PAN India", v: "Hiring reach" },
            ].map((s) => (
              <div key={s.v} data-testid={`stat-${s.v.replace(/\s/g, "-").toLowerCase()}`}>
                <div className="font-display text-4xl md:text-5xl font-medium text-forest">
                  {s.k}
                </div>
                <div className="mt-2 text-xs uppercase tracking-[0.2em] text-muted2">
                  {s.v}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES BENTO */}
      <section id="services" className="bg-[#F1EAEC] py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-16">
            <SectionHeading
              overline="What we do"
              title="One partner across the entire HR value chain."
              description="Whether you need one senior hire or a fully embedded talent team, we structure the engagement to match how you operate."
            />
            <Link to="/services" data-testid="services-viewall" className="btn-ghost self-start">
              All services <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
            {SERVICES.map((s, i) => {
              const span =
                i === 0 ? "md:col-span-7" :
                i === 1 ? "md:col-span-5" :
                i === 2 ? "md:col-span-4" :
                i === 3 ? "md:col-span-8" :
                i === 4 ? "md:col-span-6" : "md:col-span-6";
              return (
                <Tilt3D key={s.slug} max={4} scale={1.015} className={`${span}`}>
                  <Link
                    to={`/services#${s.slug}`}
                    data-testid={`home-service-${s.slug}`}
                    className="block bg-white border border-black/5 p-8 md:p-10 transition-shadow hover:shadow-2xl group h-full"
                  >
                    <div className="overline text-terracotta">{s.tag}</div>
                    <h3 className="font-display text-2xl md:text-3xl font-medium mt-4 text-ink group-hover:text-forest transition-colors">
                      {s.title}
                    </h3>
                    <p className="mt-4 text-muted2 leading-relaxed">{s.excerpt}</p>
                    <div className="mt-6 flex items-center gap-2 text-sm text-forest font-medium">
                      Learn more <ArrowUpRight className="w-4 h-4" />
                    </div>
                  </Link>
                </Tilt3D>
              );
            })}
          </div>
        </div>
      </section>

      {/* VISION, MISSION & FOUNDER'S MESSAGE */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-16">
          <div className="lg:col-span-5">
            <div className="overline text-terracotta mb-4">Vision, Mission &amp; Founder&rsquo;s Message</div>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-medium text-ink leading-[1.05] tracking-tight">
              People are the foundation of every successful business.
            </h2>
          </div>
          <div className="lg:col-span-7 grid gap-8">
            <div className="border-l-2 border-forest pl-6">
              <div className="overline text-forest">Our vision</div>
              <p className="mt-3 text-lg text-ink leading-relaxed">
                To become a trusted recruitment partner by connecting exceptional
                talent with the right opportunities.
              </p>
            </div>
            <div className="border-l-2 border-forest pl-6">
              <div className="overline text-forest">Our mission</div>
              <p className="mt-3 text-lg text-ink leading-relaxed">
                Deliver reliable, efficient, and customised hiring solutions that
                help organisations grow while empowering professionals to build
                successful careers.
              </p>
            </div>
            <div className="border-l-2 border-terracotta pl-6 bg-[#F1EAEC] p-6">
              <div className="overline text-terracotta">Founder&rsquo;s message</div>
              <p className="mt-3 text-ink leading-relaxed">
                At Novacore Services Pvt Ltd, we believe that people are the foundation
                of every successful business. We are committed to integrity, excellence,
                and creating lasting value for both our clients and candidates.
              </p>
              <div className="mt-6 pt-5 border-t border-forest/15 flex flex-wrap gap-8">
                <div>
                  <div className="font-display text-lg font-semibold text-ink">Ankit Sharma</div>
                  <div className="text-xs uppercase tracking-[0.18em] text-terracotta mt-1">Founder</div>
                  <p className="text-sm text-muted2 mt-2 max-w-xs leading-relaxed">
                    Leads Novacore&rsquo;s vision and client partnerships across India.
                  </p>
                </div>
                <div>
                  <div className="font-display text-lg font-semibold text-ink">Parwati Sharma</div>
                  <div className="text-xs uppercase tracking-[0.18em] text-terracotta mt-1">Co-Founder</div>
                  <p className="text-sm text-muted2 mt-2 max-w-xs leading-relaxed">
                    Drives operations, delivery quality, and candidate experience.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WHY US */}
      <section className="py-24 md:py-32 bg-[#F1EAEC]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-16">
          <div className="lg:col-span-5">
            <div className="aspect-[4/5] overflow-hidden shadow-xl">
              <img
                src={STAFFING_IMG}
                alt="Novacore recruitment team"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
          <div className="lg:col-span-7">
            <SectionHeading
              overline="Why choose Novacore"
              title="Recruitment done right — from brief to onboarding."
              description="We work as an extension of your HR team, not a placement vendor. Here's what that looks like in practice."
            />
            <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6">
              {[
                { t: "Fast turnaround time", d: "Curated shortlists within 48–72 hours for most mandates, with a median close time of 12 days." },
                { t: "Pre-screened candidates", d: "Every profile is vetted for skills, culture-fit and intent before it lands on your desk." },
                { t: "Dedicated recruitment team", d: "A named account partner and recruiter pod owns your requisitions end-to-end." },
                { t: "Customised hiring solutions", d: "Permanent, contract, RPO, or hybrid — we structure the engagement to match how you operate." },
                { t: "PAN India talent network", d: "Active candidate database across metro, tier-2 and tier-3 cities — plus remote-first pipelines." },
              ].map((f) => (
                <div key={f.t} className="border-t border-forest/20 pt-6">
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 mt-0.5 text-forest shrink-0" strokeWidth={1.5} />
                    <div>
                      <div className="font-display text-lg font-medium text-ink">{f.t}</div>
                      <div className="text-sm text-muted2 mt-2 leading-relaxed">{f.d}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* INDUSTRIES MARQUEE */}
      <section className="py-16 border-y border-hairline bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex flex-col md:flex-row md:items-center gap-8">
          <div className="md:w-1/4">
            <div className="overline text-terracotta mb-2">Industries</div>
            <div className="font-display text-2xl text-ink">We work across sectors that hire the most.</div>
          </div>
          <div className="md:w-3/4 overflow-hidden relative">
            <div className="flex gap-12 animate-marquee whitespace-nowrap">
              {[...INDUSTRIES, ...INDUSTRIES].map((ind, i) => (
                <span key={i} className="font-display text-2xl md:text-3xl text-ink/70">
                  {ind.name} <span className="text-terracotta mx-6">·</span>
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-24 md:py-32 bg-[#F1EAEC]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <SectionHeading
            overline="In their words"
            title="The teams we build for tend to keep building with us."
          />
          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6">
            {TESTIMONIALS.map((t, i) => (
              <Tilt3D key={i} max={4} scale={1.01}>
                <div
                  data-testid={`testimonial-${i}`}
                  className="bg-white p-8 border border-black/5 flex flex-col h-full shadow-sm"
                >
                  <div className="font-display text-4xl text-terracotta leading-none">“</div>
                  <p className="mt-4 text-ink leading-relaxed">{t.quote}</p>
                  <div className="mt-8 pt-6 border-t border-hairline text-sm text-muted2">
                    {t.author}
                  </div>
                </div>
              </Tilt3D>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="bg-forest text-bone p-10 md:p-20 relative overflow-hidden">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end relative z-10">
              <div className="md:col-span-8">
                <div className="overline text-terracotta-soft mb-6">Let&rsquo;s talk</div>
                <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-medium leading-[1.05] tracking-tight">
                  Have a hire, a payroll cycle, or an audit due?
                  <br />
                  <span className="italic font-light">Start with a 30-minute call.</span>
                </h2>
              </div>
              <div className="md:col-span-4 flex md:justify-end">
                <Link to="/contact" data-testid="home-final-cta" className="btn-accent">
                  Book a discovery call <ArrowUpRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
